import React from 'react';
import { getColor, getCycleColor } from './colorUtils';
import type { ColorSet } from './colorUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProcessFlowStep {
  label: string;
  description?: string;
  color?: string;
}

interface ProcessFlowProps {
  title?: string;
  steps: ProcessFlowStep[];
  variant?: 'numbered' | 'labeled';
  activeStep?: number;
  compact?: boolean;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Small right-pointing arrow rendered between steps (horizontal layout). */
const HorizontalArrow: React.FC<{ fill: string }> = ({ fill }) => (
  <div className="hidden md:flex items-center justify-center flex-shrink-0 px-1">
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="w-6 h-6"
    >
      <path
        d="M5 12h14m0 0l-5-5m5 5l-5 5"
        stroke={fill}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

/** Small down-pointing arrow rendered between steps (vertical / mobile layout). */
const VerticalArrow: React.FC<{ fill: string }> = ({ fill }) => (
  <div className="flex md:hidden items-center justify-center py-1">
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="w-6 h-6"
    >
      <path
        d="M12 5v14m0 0l-5-5m5 5l5-5"
        stroke={fill}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

/** A single step box. */
const StepBox: React.FC<{
  step: ProcessFlowStep;
  index: number;
  colorSet: ColorSet;
  isActive: boolean;
  variant: 'numbered' | 'labeled';
  compact: boolean;
}> = ({ step, index, colorSet, isActive, variant, compact }) => {
  const numberSize = compact ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-base';
  const labelSize = compact ? 'text-xs' : 'text-sm';
  const descSize = compact ? 'text-[10px]' : 'text-xs';
  const padding = compact ? 'px-3 py-2' : 'px-4 py-3';

  return (
    <div
      className={[
        'flex flex-col items-center text-center rounded-xl transition-shadow',
        padding,
        isActive
          ? `ring-2 ring-offset-2 ${colorSet.border} shadow-md`
          : 'shadow-sm',
        colorSet.light,
        'min-w-0',
      ].join(' ')}
    >
      {/* Number badge or colored bar */}
      {variant === 'numbered' ? (
        <div
          className={[
            'flex items-center justify-center rounded-full font-bold text-white mb-2',
            numberSize,
            colorSet.bg,
          ].join(' ')}
        >
          {index + 1}
        </div>
      ) : (
        <div className={`w-10 h-1 rounded-full ${colorSet.bg} mb-2`} />
      )}

      {/* Label */}
      <span
        className={`font-semibold ${labelSize} text-gray-900 leading-tight`}
      >
        {step.label}
      </span>

      {/* Description */}
      {step.description && (
        <span
          className={`${descSize} text-gray-500 mt-1 leading-snug max-w-[10rem]`}
        >
          {step.description}
        </span>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ProcessFlow: React.FC<ProcessFlowProps> = ({
  title,
  steps,
  variant = 'numbered',
  activeStep,
  compact = false,
}) => {
  if (!steps || steps.length === 0) {
    return null;
  }

  // Resolve a ColorSet for each step. If the step provides a color key, use
  // getColor; otherwise fall back to the cycling palette.
  const resolvedColors: ColorSet[] = steps.map((step, i) =>
    step.color ? getColor(step.color) : getCycleColor(i),
  );

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden my-8">
      {/* Optional title header */}
      {title && (
        <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-800 m-0">
            {title}
          </h3>
        </div>
      )}

      {/* Steps container */}
      <div
        className={[
          'flex',
          // Horizontal on md+, vertical on mobile
          'flex-col md:flex-row',
          'items-center md:items-start',
          'justify-center',
          compact ? 'gap-0 p-4' : 'gap-0 p-5 md:p-6',
        ].join(' ')}
      >
        {steps.map((step, index) => {
          const colorSet = resolvedColors[index];
          const isLast = index === steps.length - 1;
          const isActive = activeStep === index;

          return (
            <React.Fragment key={index}>
              <StepBox
                step={step}
                index={index}
                colorSet={colorSet}
                isActive={isActive}
                variant={variant}
                compact={compact}
              />

              {/* Arrow connector (skip after last step) */}
              {!isLast && (
                <>
                  <HorizontalArrow fill={resolvedColors[index + 1].fill} />
                  <VerticalArrow fill={resolvedColors[index + 1].fill} />
                </>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default ProcessFlow;
